import { clasesDeControl } from '@/ui/Field';
import { cn } from '@/lib/cn';

export interface Chip {
  clave: string;
  texto: string;
  icono: string;
  /** Solo aparece si su contador es mayor que cero. */
  soloSiHay?: boolean;
}

/**
 * La barra de filtros: unos cuantos chips y una búsqueda.
 *
 * La comparten «Mis solicitudes» y «Bandeja» **con el mismo vocabulario a
 * propósito** —«En curso» / «Cerradas»— para que gestor y solicitante hablen de
 * lo mismo cuando se llaman por teléfono.
 *
 * Los chips son `<button>` y no `<div>` con `onclick`: un div no recibe foco, no
 * responde a Enter y el lector de pantalla no lo anuncia. `aria-pressed` dice
 * cuál está puesto, que es lo que el `.active` de la versión vanilla solo decía
 * con color.
 */
export function Filtros({
  chips, activo, cuentas, onElegir, busqueda, onBuscar, etiquetaBusqueda, marcador,
}: {
  chips: Chip[];
  activo: string;
  cuentas?: Record<string, number>;
  onElegir: (clave: string) => void;
  busqueda: string;
  onBuscar: (v: string) => void;
  etiquetaBusqueda: string;
  marcador: string;
}) {
  const visibles = chips.filter((c) => !c.soloSiHay || (cuentas?.[c.clave] ?? 0) > 0);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap gap-2">
        {visibles.map((c) => {
          const puesto = activo === c.clave;
          const n = cuentas?.[c.clave];
          return (
            <button
              key={c.clave}
              type="button"
              aria-pressed={puesto}
              onClick={() => onElegir(c.clave)}
              className={cn(
                'inline-flex min-h-9 items-center gap-2 rounded-full border px-3 text-2 font-semibold',
                'transition-colors focus-visible:focus-anillo',
                'pointer-coarse:min-h-11',
                puesto
                  ? 'border-brand bg-brand text-on-brand'
                  : 'border-line bg-surface text-ink-2 hover:bg-sunken',
              )}
            >
              <i className={cn('bi', c.icono)} aria-hidden="true" />
              {c.texto}
              {n != null && (
                <span className={cn('tabular-nums', puesto ? 'opacity-80' : 'text-muted')}>
                  {n}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="relative min-w-56 flex-1 sm:max-w-72">
        <label className="sr-only" htmlFor="sol-busqueda">{etiquetaBusqueda}</label>
        <i className="bi bi-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-2 text-muted"
           aria-hidden="true" />
        {/* Control suelto y no `<Field>`: ya tiene etiqueta —la `sr-only` de
            arriba— y un `Field` pintaría una segunda encima del recuadro. */}
        <input
          id="sol-busqueda"
          type="search"
          className={cn(clasesDeControl, 'pl-9')}
          placeholder={marcador}
          value={busqueda}
          onChange={(e) => onBuscar(e.target.value)}
        />
      </div>
    </div>
  );
}
